// lib/auth-client.ts
import { clearAllDrafts } from "./composer-draft";

// Base URL of the dashboard app that owns authentication. e2e is a thin client
// of the dashboard's /api/auth/* endpoints — it holds no auth logic of its own.
export const DASHBOARD_URL =
  process.env.NEXT_PUBLIC_DASHBOARD_URL?.replace(/\/$/, "") ||
  "https://dashboard.vucar.vn";

// End the current session. Mirrors the login call in login-screen.tsx: POST to the
// dashboard (the auth owner) with the shared .vucar.vn cookie, which the dashboard
// clears, then hard-reload so the server gate in app/layout.tsx re-evaluates the
// (now missing) session and renders <LoginScreen />.
//
// Note: in local `next dev`, DEV_AUTH_BYPASS re-authenticates as a dev user after
// the cookie is gone, so logout only visibly returns to the login screen in a real
// deploy or with DEV_AUTH_BYPASS=false. See lib/auth-server.ts.
export async function logout(): Promise<void> {
  // Máy dùng chung: người đăng nhập sau không thấy chữ gõ dở của người trước.
  clearAllDrafts();
  try {
    await fetch(`${DASHBOARD_URL}/api/auth/logout`, {
      method: "POST",
      credentials: "include",
    });
  } catch {
    // A network hiccup shouldn't trap the user in the app — fall through to the
    // reload and let the server gate decide based on the current cookie state.
  }
  window.location.href = "/";
}

// ── Google SSO — giống GoogleSignInButton của dashboard (/dang-nhap) ─────────
// Không fetch thẳng dashboard: /api/auth/[...all] (Better Auth catch-all) không
// trả CORS header nên preflight bị chặn — đi qua proxy same-origin
// /api/workspace/auth/sso, nhận lại {url} rồi navigate tới Google. Callback của
// dashboard set cookie session .vucar.vn rồi redirect về origin này — server
// gate (app/layout.tsx → getCurrentUser) nhận session như login thường.
export async function signInWithGoogle(): Promise<void> {
  const res = await fetch("/api/workspace/auth/sso", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ provider: "google" }),
  });
  const data = (await res.json().catch(() => ({}))) as {
    url?: string;
    error?: string;
  };
  if (!res.ok || !data.url) {
    throw new Error(
      data.error ?? "Đăng nhập với Google thất bại. Vui lòng thử lại.",
    );
  }
  window.location.href = data.url;
}

// Bản dịch tiếng Việt của các mã lỗi OAuth callback — port từ
// apps/dashboard/src/lib/auth/oauth-errors.ts để hai màn login hiển thị giống
// nhau. Dashboard redirect về <errorCallbackURL>?error=<code>.
const DEACTIVATED_MESSAGE =
  "Tài khoản đã bị vô hiệu hóa. Vui lòng liên hệ quản trị viên.";

const OAUTH_ERROR_MESSAGES: Record<string, string> = {
  banned: DEACTIVATED_MESSAGE,
  BANNED_USER: DEACTIVATED_MESSAGE,
  access_denied: "Bạn đã hủy đăng nhập với Google.",
  email_not_found:
    "Tài khoản Google không cung cấp địa chỉ email. Vui lòng thử tài khoản khác.",
  email_not_verified:
    "Email của tài khoản Google chưa được xác minh. Vui lòng thử tài khoản khác.",
  email_does_not_match:
    "Email tài khoản Google không khớp với tài khoản đang đăng nhập.",
  account_already_linked_to_different_user:
    "Tài khoản Google này đã liên kết với một người dùng khác.",
  unable_to_link_account:
    "Không thể liên kết tài khoản Google. Vui lòng liên hệ quản trị viên.",
};

export function oauthErrorMessage(code: string | null | undefined): string | null {
  if (!code) return null;
  return (
    OAUTH_ERROR_MESSAGES[code] ??
    `Đăng nhập với Google thất bại (${code}). Vui lòng thử lại hoặc dùng email và mật khẩu.`
  );
}
